import { sessionTokens, type SessionTokens } from '@occ/contracts';
import { API_URL } from '../config/env';
import { clearSession, isExpired, loadSession, saveSession } from '../auth/session';
import { ROUTES } from './routes';

/**
 * Cliente HTTP del panel.
 *
 * Concentra tres cosas que no deben repetirse por pantalla: el token en la
 * cabecera, la renovación cuando caduca y la traducción de un error de red o
 * de un 4xx en algo que la UI pueda pintar. En modo simulado desvía la llamada
 * al doble de pruebas **antes** de tocar la red, así que el resto del panel no
 * sabe si hay backend o no.
 */

export class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/** El panel valida las respuestas con los esquemas de `@occ/contracts`. */
type Parser<T> = { parse: (value: unknown) => T };

export type Query = Record<string, string | number | boolean | undefined | null>;

export type RequestOptions<T> = {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  query?: Query;
  body?: unknown;
  schema?: Parser<T>;
  /** `false` en login y refresh: todavía no hay token que mandar. */
  auth?: boolean;
};

const buildQuery = (query: Query | undefined): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

const messageFromBody = (status: number, body: unknown): string => {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = (body as { message: unknown }).message;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join(', ');
  }
  return `HTTP ${status}`;
};

/**
 * Renovación con un solo vuelo: si tres peticiones caducan a la vez, solo una
 * canjea el refresh y las otras esperan su resultado. Sin esto, el backend
 * vería tres canjes del mismo refresh rotatorio y revocaría la sesión.
 */
let inFlightRefresh: Promise<SessionTokens | null> | null = null;

const refreshTokens = async (): Promise<SessionTokens | null> => {
  const session = loadSession();
  if (!session) return null;

  inFlightRefresh ??= (async () => {
    try {
      const tokens = await rawRequest({
        method: 'POST',
        path: ROUTES.refresh,
        body: { refreshToken: session.refreshToken },
        schema: sessionTokens,
        auth: false,
      });
      saveSession(tokens);
      return tokens;
    } catch {
      clearSession();
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
};

/** Una sola vuelta: sin reintentos ni renovación. La usa el propio refresh. */
async function rawRequest<T>(options: RequestOptions<T>): Promise<T> {
  const { method, path, query, body, schema, auth = true } = options;
  const url = `${path}${buildQuery(query)}`;
  const session = auth ? loadSession() : null;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (session) headers.Authorization = `Bearer ${session.accessToken}`;

  let response: Response;
  try {
    response = await fetch(`${API_URL}${url}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    // Sin respuesta: el backend no está o la red se cayó. `0` distingue este
    // caso de un 5xx, que sí implica que la puerta contestó.
    throw new HttpError(0, error instanceof Error ? error.message : 'Sin conexión con el backend');
  }
  const status = response.status;
  const payload =
    response.status === 204 ? undefined : await response.json().catch(() => undefined);

  if (status >= 400) {
    throw new HttpError(status, messageFromBody(status, payload), payload);
  }

  return schema ? schema.parse(payload) : (payload as T);
}

export async function request<T>(options: RequestOptions<T>): Promise<T> {
  const session = options.auth === false ? null : loadSession();

  // Renovación anticipada: evita gastar un 401 para descubrir lo que ya sabemos.
  if (session && isExpired(session)) {
    await refreshTokens();
  }

  try {
    return await rawRequest(options);
  } catch (error) {
    const recoverable =
      error instanceof HttpError && error.status === 401 && options.auth !== false;
    if (!recoverable) throw error;

    const renewed = await refreshTokens();
    if (!renewed) throw error;
    return rawRequest(options);
  }
}

export const get = <T>(path: string, schema?: Parser<T>, query?: Query): Promise<T> =>
  request<T>({ method: 'GET', path, schema, query });

export const post = <T>(path: string, body?: unknown, schema?: Parser<T>): Promise<T> =>
  request<T>({ method: 'POST', path, body, schema });

export const patch = <T>(path: string, body?: unknown, schema?: Parser<T>): Promise<T> =>
  request<T>({ method: 'PATCH', path, body, schema });

export const put = <T>(path: string, body?: unknown, schema?: Parser<T>): Promise<T> =>
  request<T>({ method: 'PUT', path, body, schema });

export const del = (path: string): Promise<void> => request<void>({ method: 'DELETE', path });

/**
 * Traduce un fallo de la puerta a algo que un técnico pueda leer.
 *
 * Los códigos no son decorativos y cada uno lleva a una acción distinta, así que
 * se distinguen en vez de colapsarlos en "error": un 409 se arregla actualizando
 * el agente de ese nodo, un 501 esperando a que el módulo entre, y un 503
 * encendiendo la máquina. Un "algo falló" obligaría a abrir los logs para saber
 * cuál de las tres.
 */
export const describeHttpError = (error: unknown): string => {
  if (!(error instanceof HttpError)) {
    return error instanceof Error ? error.message : 'La operación falló';
  }
  switch (error.status) {
    case 0:
      return 'Sin conexión con el backend. Comprueba que la puerta está levantada.';
    case 401:
      return 'La sesión caducó. Vuelve a entrar.';
    case 403:
      return `Denegado por la puerta: ${error.message}`;
    case 404:
      return `No existe: ${error.message}`;
    case 409:
      // 409 está sobrecargado y sus dos usos llevan a acciones opuestas: «el
      // agente de este nodo no declara la capacidad» (hay que actualizar el
      // agente) y «el archivo ya existe en el destino» (hay que marcar
      // sobreescribir). Los mensajes del backend ya distinguen los dos casos, así
      // que se pasan tal cual: prefijarlos con una interpretación acertaría la
      // mitad de las veces y confundiría la otra mitad.
      return error.message;
    case 501:
      return `Todavía no disponible: ${error.message}`;
    case 502:
    case 503:
      return `El nodo no está alcanzable ahora mismo: ${error.message}`;
    default:
      return error.message;
  }
};

/**
 * Si el motivo es "esto no existe todavía" y no "esto falló".
 *
 * Sirve para elegir el tono de la pantalla: una función que llega en otra fase
 * se anuncia, no se pinta en rojo como si algo se hubiera roto.
 */
export const isPendingFeature = (error: unknown): boolean =>
  error instanceof HttpError && (error.status === 501 || error.status === 404);
